import React, { createContext, useState, useEffect, useContext, useCallback } from "react";

import { unwrapApiData, unwrapApiRecord } from "../../shared/utils/apiResponse";
import { hydrateProfileWithWalletPayload, Profile, WalletPayload } from "../../utils/walletSnapshot";
import { handleGetUserProfile, handleGetUserWallets } from "../userControllers/profileController";

interface UserContextType {
    userProfile: Profile | null;
    loadingProfile: boolean;
    refreshUserProfile: () => Promise<void>;
    saveUserProfile: (profile: Profile | null) => void;
}

// Create UserContext
const UserContext = createContext<UserContextType | undefined>(undefined);

// Custom hook to access the context
export const useUserContext = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error("useUserContext must be used within a UserProvider");
    }
    return context;
};

interface UserProviderProps {
    children: React.ReactNode;
}

// Create a Provider component
export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
    const getCachedProfile = () => {
        if (typeof globalThis === "undefined") return null;
        try {
            const cached = globalThis.localStorage.getItem("saca_user_profile");
            return cached ? JSON.parse(cached) : null;
        } catch (error) {
            console.warn("Failed to parse cached profile:", error);
            return null;
        }
    };

    const cachedProfile = getCachedProfile();
    const [userProfile, setUserProfile] = useState<Profile | null>(() => cachedProfile);
    const [loadingProfile, setLoading] = useState(!cachedProfile);

    const persistProfile = useCallback((profile: Profile | null) => {
        setUserProfile(profile || null);
        if (typeof globalThis === "undefined") return;
        try {
            if (profile) {
                globalThis.localStorage.setItem("saca_user_profile", JSON.stringify(profile));
            } else {
                globalThis.localStorage.removeItem("saca_user_profile");
            }
        } catch (error) {
            console.warn("Unable to persist profile to localStorage:", error);
        }
    }, []);

    const fetchUserProfile = useCallback(async () => {
        const token =
            typeof globalThis !== "undefined" ? localStorage.getItem("saca_token") : null;

        if (!token) {
            persistProfile(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const profileResponse = await handleGetUserProfile<Profile>();
            const walletsResponse = await handleGetUserWallets().catch(() => null);
            const profileData = unwrapApiRecord<Profile>(profileResponse);
            if (Object.keys(profileData).length > 0) {
                const walletPayload =
                    unwrapApiData<WalletPayload>(walletsResponse ?? null) ?? null;
                const hydrated = walletsResponse
                    ? hydrateProfileWithWalletPayload({
                        profile: profileData,
                        payload: walletPayload,
                    })
                    : profileData;
                persistProfile(hydrated);
            } else {
                persistProfile(null);
            }
        } catch (error) {
            console.error("Error fetching user:", error);
        } finally {
            setLoading(false);
        }
    }, [persistProfile]);

    useEffect(() => {
        fetchUserProfile();
    }, [fetchUserProfile]);

    return (
        <UserContext.Provider
            value={{
                userProfile,
                loadingProfile,
                refreshUserProfile: fetchUserProfile, // Expose the function here
                saveUserProfile: persistProfile,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};
